import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { Action, useQueryParams } from '@/hooks/queryParams';
import { ModifyQueryParamsAction } from '@/hooks/queryParams/useModifyQueryParams';
import {
    QueryParams as NotificationsPageQueryParams,
    SetActiveIdCallback,
} from '@/pages/Notifications/notifications.types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getTotalSavedSearches,
    isSavedSearchesLoading,
    onSavedSearchRecentFilterChange,
    savedSearchSearchesSelector,
    subscribeAllSavedSearchEmail,
    toggleSavedSearchEmail,
    unsubscribeAllSavedSearchEmail,
} from '@/redux/modules/savedSearch';
import { PaginationFilter } from '@/types/PaginationFilter';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SavedSearchNotificationFilter } from '../ForYou/ForYouPage/components/MySavedSearch/components/SavedSearchNotificationFilter';
import { SavedSearchTable } from './Components/SavedSearchTable';
import { Section } from './Components/Section';
import { StatusCheckbox } from './Components/StatusCheckbox';

type QueryParams = { page?: string; pageSize?: string };

type Props = {
    activeId: NotificationsPageQueryParams['section'];
    filters: PaginationFilter;
    queryParams: QueryParams;
    setActiveId: SetActiveIdCallback;
};

export const MySavedSearchPageManage = ({ activeId, filters, setActiveId }: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const totalSavedSearchCount = useAppSelector(getTotalSavedSearches);
    const isLoading = useAppSelector(isSavedSearchesLoading);
    const savedSearches = useAppSelector(savedSearchSearchesSelector);
    const { modifyQueryParams } = useQueryParams();
    const [queryParams] = useSearchParams();
    const section = queryParams.get('section');

    const toggleEmail = useCallback((savedSearchId) => dispatch(toggleSavedSearchEmail(savedSearchId)), [dispatch]);

    useEffect(() => {
        if (!section) {
            modifyQueryParams({
                action: Action.ADD,
                query: { section: 'searches' },
            });
        }
    }, [modifyQueryParams, section]);

    const onFilterChange = useCallback(
        (filters: PaginationFilter) => {
            // modify url with pageSize and page query params
            modifyQueryParams({
                action: ModifyQueryParamsAction.ADD,
                query: { page: filters.page, pageSize: filters.pageSize },
            });
            dispatch(onSavedSearchRecentFilterChange(filters));
        },
        [dispatch, modifyQueryParams]
    );

    const showPager = typeof filters.pageSize === 'number' && totalSavedSearchCount > filters.pageSize;

    const filterComponent = (
        <SavedSearchNotificationFilter
            filters={filters}
            onFilterChange={onFilterChange}
        />
    );

    return (
        <Section
            activeId={activeId}
            component={
                <div className="mt-0 mr-auto mb-xs block w-full max-w-[1200px] self-center border-b border-border-secondary pb-lg">
                    {showPager ? (
                        <Pagination
                            filter={filterComponent}
                            onChange={onFilterChange}
                            paginationFilter={{
                                ...filters,
                                totalRecords: totalSavedSearchCount,
                            }}
                            shouldModifyQueryParams
                        >
                            <SavedSearchTable
                                searches={savedSearches}
                                toggleSavedSearchEmail={toggleEmail}
                            />
                        </Pagination>
                    ) : (
                        <>
                            {filterComponent}
                            <>
                                {!isLoading && totalSavedSearchCount === 0 ? (
                                    <div className="mt-lg text-center">
                                        <BodySecondary>{formatMessage(['search_alerts', 'no_followed'])}</BodySecondary>
                                    </div>
                                ) : (
                                    <SavedSearchTable
                                        searches={savedSearches}
                                        toggleSavedSearchEmail={toggleEmail}
                                    />
                                )}
                            </>
                        </>
                    )}
                </div>
            }
            count={totalSavedSearchCount}
            email={
                /*  Slice because the last one is an unsubscribe button */
                <StatusCheckbox
                    array={savedSearches}
                    category="saved search"
                    onChange={(checked) => {
                        checked ? dispatch(unsubscribeAllSavedSearchEmail()) : dispatch(subscribeAllSavedSearchEmail());
                    }}
                    selector={(search) => search.sendAlert}
                    type="email"
                />
            }
            id="searches"
            setActiveId={setActiveId}
        />
    );
};
